/**
 * Game.ts — Central orchestrator.
 *
 * Owns the Three.js renderer, scene, camera, clock, and the core
 * requestAnimationFrame loop.  Delegates subsystems to specialised
 * modules (PlayerPlane, Controls, World, Missions, UI).
 */
import * as THREE from 'three';
import { PlayerPlane } from './PlayerPlane';
import { Controls, loadDesktopSettings } from './Controls';
import { World } from './World';
import { MissionManager } from './Missions';
import { UI } from './UI';
export class Game {
    constructor() {
        /* ---- State ---- */
        this.running = false;
        this.score = 0;
        this.fuel = 100; // percentage
        this.dayNightCycle = 0; // 0‥1
        this.windDir = new THREE.Vector3();
        this.windStrength = 0;
        this.startMissionIndex = 0;
        /* ---- Pause state ---- */
        this.paused = false;
        /**
         * Optional hook called whenever the game ENTERS the paused/menu state.
         * main.ts uses this to sync the MenuGamepadPoller so the same button press
         * that triggered the pause doesn't immediately re-resume the game.
         */
        this.onPause = null;
        /* ---------- Main loop ---------- */
        this.loop = () => {
            if (!this.running)
                return;
            requestAnimationFrame(this.loop);
            const dt = Math.min(this.clock.getDelta(), 0.05); // cap to avoid spiral of death
            /* Wind (gentle random drift) */
            this.updateWind(dt);
            /* Day / night cycle: full cycle = 600 s (10 min) */
            this.dayNightCycle = (this.dayNightCycle + dt / 600) % 1;
            this.world.updateDayNight(this.dayNightCycle);
            /* Fuel drain (very gentle — ≈4 min of full-throttle flight) */
            this.fuel -= dt * (0.15 + this.player.throttle * 0.25);
            if (this.fuel <= 0) {
                this.fuel = 0;
                this.gameOver();
                return;
            }
            /* Sub-updates */
            this.controls.update(dt);
            this.player.update(dt);
            this.world.update(dt);
            this.missions.update(dt);
            this.ui.update(dt);
            /* Camera */
            this.updateCamera(dt);
            /* Render */
            this.renderer.render(this.scene, this.camera);
        };
        /* ---------- Camera follow ---------- */
        this._camIdeal = new THREE.Vector3();
        this._camLookAt = new THREE.Vector3();
        this._camCurrent = new THREE.Vector3();
        /* ---------- Wind ---------- */
        this._windTimer = 0;
        /* Renderer */
        const canvas = document.getElementById('game-canvas');
        this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.shadowMap.enabled = true;
        this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
        this.renderer.toneMappingExposure = 1.0;
        /* Scene */
        this.scene = new THREE.Scene();
        this.scene.fog = new THREE.FogExp2(0x88bbee, 0.00025);
        /* Camera (will follow the plane) */
        this.camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 1, 12000);
        this.camera.position.set(0, 60, -100);
        /* Clock */
        this.clock = new THREE.Clock(false);
        /* Resize handler */
        window.addEventListener('resize', () => this.onResize());
    }
    /* ---------- Public API ---------- */
    /** Called once when the player presses "Take Off". */
    start() {
        this.score = 0;
        this.fuel = 100;
        this.dayNightCycle = 0.25; // start at sunrise-ish
        /* Build subsystems (order matters) */
        this.world = new World(this);
        this.player = new PlayerPlane(this);
        this.controls = new Controls(this);
        this.missions = new MissionManager(this, this.startMissionIndex);
        this.ui = new UI(this);
        this.ui.show();
        this.running = true;
        this.clock.start();
        this.loop();
    }
    /** Full reset after game-over. */
    restart() {
        this.dispose();
        this.start();
    }
    /** Toggle between paused (show start screen) and running. */
    togglePause() {
        if (this.paused) {
            // Resume
            this.paused = false;
            document.getElementById('start-screen').classList.add('hidden');
            document.getElementById('restart-pause-btn').classList.add('hidden');
            const btn = document.getElementById('start-btn');
            btn.textContent = 'Take Off';
            // Reload desktop settings in case they were changed while paused
            if (this.controls && !this.controls.isMobile) {
                this.controls.desktopSettings = loadDesktopSettings();
            }
            this.clock.start();
            this.running = true;
            this.loop();
            // Re-acquire pointer lock on desktop
            if (!this.controls?.isMobile && !this.controls?.gamepadActive) {
                this.renderer.domElement.requestPointerLock();
            }
        }
        else if (this.running) {
            // Pause
            this.paused = true;
            this.running = false;
            this.clock.stop();
            if (document.pointerLockElement)
                document.exitPointerLock();
            document.getElementById('start-screen').classList.remove('hidden');
            const btn = document.getElementById('start-btn');
            btn.textContent = 'Resume Flight';
            document.getElementById('restart-pause-btn').classList.remove('hidden');
            /* Notify external listeners (e.g. MenuGamepadPoller) that we paused */
            this.onPause?.();
        }
    }
    /** Stop the session and return to the start screen without restarting. */
    returnToMenu() {
        if (!this.running && !this.paused)
            return;
        this.paused = false;
        this.running = false;
        this.clock.stop();
        this.ui?.hide();
        if (document.pointerLockElement)
            document.exitPointerLock();
        /* Release orientation lock if held */
        try {
            const orientation = screen.orientation;
            if (orientation?.unlock)
                orientation.unlock();
        }
        catch { /* ignore */ }
        /* Hide any open overlays */
        for (const id of ['mission-complete', 'game-over']) {
            document.getElementById(id).classList.add('hidden');
        }
        this.dispose();
        document.getElementById('start-screen').classList.remove('hidden');
        document.getElementById('start-btn').textContent = 'Take Off';
        document.getElementById('restart-pause-btn').classList.add('hidden');
    }
    /** Advance to the next mission. */
    nextMission(selectedMissionIndex) {
        this.missions.advance(selectedMissionIndex);
    }
    /** Set the mission index that will be used when the next run starts. */
    setStartMission(missionIndex) {
        this.startMissionIndex = missionIndex;
    }
    updateCamera(dt) {
        const p = this.player;
        // Desired position: behind and above the plane
        const back = new THREE.Vector3(0, 0, -1).applyQuaternion(p.mesh.quaternion);
        const up = new THREE.Vector3(0, 1, 0).applyQuaternion(p.mesh.quaternion);
        this._camIdeal.copy(p.mesh.position)
            .add(back.multiplyScalar(-18))
            .add(up.multiplyScalar(6));
        // Smooth lerp
        const speed = 3.5;
        this._camCurrent.lerp(this._camIdeal, 1 - Math.exp(-speed * dt));
        this.camera.position.copy(this._camCurrent);
        // Look-at target: slightly ahead of the plane
        const fwd = new THREE.Vector3(0, 0, -1).applyQuaternion(p.mesh.quaternion);
        this._camLookAt.copy(p.mesh.position).add(fwd.multiplyScalar(20));
        this.camera.lookAt(this._camLookAt);
    }
    updateWind(dt) {
        this._windTimer -= dt;
        if (this._windTimer <= 0) {
            this._windTimer = 4 + Math.random() * 6;
            this.windDir.set(Math.random() - 0.5, 0, Math.random() - 0.5).normalize();
            this.windStrength = Math.random() * 6;
        }
    }
    /* ---------- Game-over ---------- */
    gameOver() {
        this.running = false;
        this.clock.stop();
        document.getElementById('game-over-score').textContent = `Final score: ${this.score}`;
        document.getElementById('game-over').classList.remove('hidden');
        this.ui.hide();
    }
    /* ---------- Helpers ---------- */
    /** Add points with a celebratory HUD message. */
    addScore(points, message) {
        this.score += points;
        this.ui.flashMessage(message);
    }
    /** Refuel the tank (clamped to 100). */
    refuel(amount) {
        this.fuel = Math.min(100, this.fuel + amount);
    }
    onResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }
    /** Tear down the scene so restart doesn't leak memory. */
    dispose() {
        this.controls?.dispose();
        // Remove everything from scene
        while (this.scene.children.length > 0) {
            const c = this.scene.children[0];
            this.scene.remove(c);
        }
    }
}
